import { CreateFocusCss } from '../../../styles'
import type { Tokens } from '../../../theming/types'
import { styles } from '../../../utilities'

export default (config: { tokens: Tokens }) => {
  const { tokens } = config
  return {
    default: {
      wrap: {
        ...tokens.fontBodyM,
        minHeight: tokens.selectionControl.size,
        minWidth: tokens.selectionControl.size,
        position: 'relative',
      },

      input: {
        ...styles.visuallyHidden,
        ...CreateFocusCss(
          {
            ...tokens.focusOuter,
            content: '""',
            display: 'block',
            height: '18px',
            insetInlineStart: 0,
            position: 'absolute',
            top: '0',
            width: '18px',
          } as const,
          (psuedoClass) => `${psuedoClass} + label > span::after`,
        ),
      },

      description: {
        marginBlock: '6px 0',
        marginInline: '30px 0',
      },

      error: {
        marginBlock: '6px 0',
        marginInline: '30px 0',
      },

      icon: {
        alignItems: 'center',
        color: tokens.iconColorDefault,
        display: 'flex',
        fontSize: '16px',
        height: tokens.icon.defaultSize,
        textAlign: 'center',
        verticalAlign: 'middle',
        width: tokens.icon.defaultSize,
      },

      label: {
        alignItems: 'flex-start',
        color: tokens.fontColorDefault,
        display: 'inline-flex',
        minHeight: tokens.selectionControl.size,
        minWidth: tokens.selectionControl.size,
        position: 'relative',

        '::before': {
          backgroundColor: tokens.formControlBgColorDefault,
          border: `1px solid ${tokens.formControlBorderColorDefault}`,
          borderRadius: '2px',
          content: '""',
          cursor: 'pointer',
          display: 'block',
          flex: '0 0 auto',
          height: tokens.selectionControl.size,
          transitionDuration: '0.2s',
          transitionProperty: 'border, background',
          transitionTimingFunction: 'ease',
          width: tokens.selectionControl.size,
        },

        '&::after': {
          background: '0 0',
          borderBlockEndColor: tokens.formControlBgColorDefault,
          borderInlineColor: tokens.formControlBgColorDefault,
          borderStyle: 'solid',
          borderWidth: '0 0 2px 2px',
          content: '""',
          height: '6px',
          insetInlineStart: '3.5px',
          pointerEvents: 'none',
          position: 'absolute',
          top: '5px',
          transform: 'rotate(-45deg)',
          transitionDuration: '0.2s',
          transitionProperty: 'border, background',
          transitionTimingFunction: 'ease',
          width: '11px',
        },

        '& > span': {
          '&::before': {
            borderColor: 'transparent',
            borderRadius: '2px',
            borderStyle: 'solid',
            borderWidth: '1px',
            content: '""',
            display: 'block',
            height: '18px',
            insetInlineStart: 0,
            position: 'absolute',
            top: 0,
            transitionDuration: '0.2s',
            transitionProperty: 'border',
            transitionTimingFunction: 'ease',
            width: '18px',

            '@media (forced-colors: active)': {
              display: 'none',
            },
          },
        },

        '&:hover': {
          cursor: 'pointer',
        },

        'input:not(:checked, :indeterminate) + &::after': {
          display: 'none',
        },

        'input:checked + &::before, input:indeterminate + &::before': {
          backgroundColor: tokens.formControlBgColorDefault,
          borderColor: tokens.formControlBorderColorDefault,
        },

        'input:checked + &::after, input:indeterminate + &::after': {
          borderBlockEndColor: tokens.formControlBorderColorSelected,
          borderInlineColor: tokens.formControlBorderColorSelected,
        },

        'input:indeterminate + &::after': {
          background: tokens.formControlBgColorDefault,
          content: '""',
          height: '2px',
          insetInlineStart: '4px',
          pointerEvents: 'none',
          position: 'absolute',
          top: '8px',
          transform: 'none',
          width: '10px',
        },

        'input:disabled + &::before': {
          cursor: 'not-allowed',
        },

        'input:disabled + &': {
          opacity: tokens.opacityDisabled,
          cursor: 'not-allowed',

          '& > span::before': {
            border: 0,
          },
        },
      },

      text: {
        alignItems: 'center',
        display: 'flex',
        minHeight: tokens.selectionControl.size,
        paddingInlineStart: '10px',
      },
    },

    beforeText: {
      icon: {
        marginInlineStart: 0,
        marginInlineEnd: '8px',
      },
    },

    afterText: {
      icon: {
        marginInlineStart: '8px',
        marginInlineEnd: 0,
      },
    },

    invalid: {
      label: {
        '::before': {
          borderColor: `${tokens.formElementInputBorderColorError}`,
        },

        'input:checked + &::before, input:checked + &::after': {
          borderColor: tokens.formElementInputBorderColorError,
        },

        'input:indeterminate + &::before, input:indeterminate + &::after': {
          borderColor: tokens.formElementInputBorderColorError,
        },
      },
    },

    hideLabel: {
      text: {
        ...styles.visuallyHidden,
        // Override 'position: absolute' so focus styles are not hidden.
        position: 'static',
      },
    },

    userSelectNone: styles.userSelectNone,
  } as const
}
